import React, { useEffect, useState } from 'react';
import {
  CardContents,
  TitleContainer,
  CardTitle,
  CancelButton,
  FormError,
  PrimaryButton,
  ButtonContainer,
  InputContainer,
  Selector,
  InputBox,
  SecondaryButton,
  Padding,
  AmountInput,
} from 'common/components/cardStyles';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { useProducerWithThunks } from 'common/hooks/useProducer';

import {
  TransferRequestChoices,
  TransferChoices,
  defaultAPIRequestState,
  api,
} from 'api';
import { ClientTransferRequest } from 'common/state/banking/transfers/actions';
import {
  doNewClientTransferRequest,
  doCancelClientTransferRequest,
  doCompleteClientTransferRequest,
  doNewClientTransfer,
} from 'common/state/banking/transfers/thunkActions';
import {
  cancelClientTransferRequestRecipe,
  completeClientTransferRequestRecipe,
  newClientTransferRecipe,
  newClientTransferRequestRecipe,
} from 'common/state/banking/transfers/recipes';
interface WithdrawModal {
  selectedRow: ClientTransferRequest;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

interface WithdrawalNewModal {
  choices: TransferRequestChoices;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

interface WithdrawalTransferModal {
  choices: TransferChoices;
  selectedRow: ClientTransferRequest;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

export function WithdrawalNewModal({
  choices,
  setShouldRefresh,
  closeModal,
}: WithdrawalNewModal) {
  const { sender, receiver, currency } = choices;

  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');

  // The currently selected sender
  const [selectedSender, setSelectedSender] = useState<null | ElementType<
    typeof sender
  >>(null);
  // The currently selected receiver
  const [selectedReceiver, setSelectedReceiver] = useState<null | ElementType<
    typeof receiver
  >>(null);
  // The currently selected currency
  const [selectedCurrency, setSelectedCurrency] = useState<null | ElementType<
    typeof currency
  >>(null);

  // Function that clears all fields
  const clearTransaction: React.MouseEventHandler = React.useCallback(() => {
    setAmount('');
    setDescription('');
    setReference('');
    setSelectedSender(null);
    setSelectedReceiver(null);
    setSelectedCurrency(null);
  }, [setAmount]);

  // The state of the new transfer request
  const [state, dispatch] = useProducerWithThunks(
    newClientTransferRequestRecipe,
    defaultAPIRequestState
  );
  // New transfer request callback for form
  const submitNewTransfer: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        doNewClientTransferRequest({
          amount,
          currency: selectedCurrency.value,
          date,
          description,
          receiver: selectedReceiver.value,
          reference,
          sender: selectedSender.value,
        })
      );
    },
    [
      dispatch,
      amount,
      selectedCurrency,
      date,
      description,
      reference,
      selectedReceiver,
      selectedSender,
    ]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitNewTransfer}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Accept Amount</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <InputContainer>
          <Padding />
          <InputBox
            type="date"
            label="Date *"
            shrinked
            onChange={(e) => setDate(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <Selector
            label="Sender *"
            shrinked
            options={sender}
            value={selectedSender}
            onChange={(e) => setSelectedSender(e)}
          />
          <Selector
            label="Receiver *"
            shrinked
            options={receiver}
            value={selectedReceiver}
            onChange={(e) => setSelectedReceiver(e)}
          />
        </InputContainer>
        <InputContainer>
          <Selector
            label="Currency *"
            shrinked
            options={currency}
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e)}
          />
          <AmountInput
            currency={selectedCurrency?.value || 'NONE'}
            shrinked
            label="Amount *"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <InputBox
            label="Description *"
            shrinked
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <InputBox
            label="Reference"
            shrinked
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </InputContainer>
        <ButtonContainer>
          <PrimaryButton
            type="submit"
            disabled={
              !amount ||
              !selectedCurrency ||
              !date ||
              !description ||
              !selectedSender
            }
          >
            Create
          </PrimaryButton>
          <SecondaryButton type="button" onClick={clearTransaction}>
            Clear
          </SecondaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}

export function WithdrawalCancelModal({
  selectedRow,
  setShouldRefresh,
  closeModal,
}: WithdrawModal) {
  const { url } = selectedRow;

  // The state of the deposit request
  const [state, dispatch] = useProducerWithThunks(
    cancelClientTransferRequestRecipe,
    defaultAPIRequestState
  );

  // Deposit request callback for form
  const submitCancelTransferRequest: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(doCancelClientTransferRequest(url));
    },
    [dispatch, url]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitCancelTransferRequest}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Cancel Transfer Request</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <ButtonContainer>
          <PrimaryButton type="submit">Confirm</PrimaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}

export function WithdrawalCompleteModal({
  selectedRow,
  setShouldRefresh,
  closeModal,
}: WithdrawModal) {
  const { url } = selectedRow;

  // The state of the deposit request
  const [state, dispatch] = useProducerWithThunks(
    completeClientTransferRequestRecipe,
    defaultAPIRequestState
  );

  // Deposit request callback for form
  const submitCancelTransferRequest: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(doCompleteClientTransferRequest(url));
    },
    [dispatch, url]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitCancelTransferRequest}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Complete Transfer Request</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <ButtonContainer>
          <PrimaryButton type="submit">Confirm</PrimaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}

export function WithdrawalTransferModal({
  choices,
  selectedRow,
  setShouldRefresh,
  closeModal,
}: WithdrawalTransferModal) {
  const { sender, currency, request, destination } = choices;
  const {
    currency: presetCurrency,
    amount: presetAmount,
    url: presetRequest,
  } = selectedRow;

  const [date, setDate] = useState('');
  const [amount, setAmount] = useState(presetAmount.replace('-', ''));
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [assets, setAssets] = useState<{
    [url: string]: Record<string, string>;
  }>({});

  useEffect(() => {
    const newUrls = new Set<string>();
    destination?.forEach((destination) => {
      if (!(destination?.value in assets)) {
        newUrls.add(destination.value);
      }
    });

    Promise.all(
      Array.from(newUrls).map((url) =>
        api.get<Record<string, string>>(url).then((response) => response.data)
      )
    ).then((_assets) =>
      setAssets(
        _assets.reduce((acc, asset) => ({ ...acc, [asset.url]: asset }), {
          ...assets,
        })
      )
    );
    //eslint-disable-next-line
  }, [destination]);

  const reducedDestinations = React.useMemo(() => {
    return destination.filter(
      (_destination) =>
        assets?.[_destination.value]?.owner === selectedRow.receiver
    );
  }, [destination, assets, selectedRow]);

  // The currently selected request
  const [selectedRequest, setSelectedRequest] = useState<null | ElementType<
    typeof request
  >>(request.find((o) => o.value === presetRequest));
  // The currently selected sender
  const [selectedSender, setSelectedSender] = useState<null | ElementType<
    typeof sender
  >>(sender.find((o) => o.value === selectedRow.sender));
  // The currently selected destination
  const [selectedDestination, setSelectedDestination] =
    useState<null | ElementType<typeof destination>>(null);

  // The currently selected currency
  const [selectedCurrency, setSelectedCurrency] = useState<null | ElementType<
    typeof currency
  >>(currency.find((o) => o.value === presetCurrency));

  // Function that clears all fields
  const clearTransaction: React.MouseEventHandler = React.useCallback(() => {
    setAmount('');
    setDescription('');
    setReference('');
    setSelectedSender(null);
    setSelectedDestination(null);
  }, [setAmount]);

  // The state of the new transfer request
  const [state, dispatch] = useProducerWithThunks(
    newClientTransferRecipe,
    defaultAPIRequestState
  );
  // New transfer request callback for form
  const submitNewTransfer: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        doNewClientTransfer({
          amount,
          currency: presetCurrency,
          date,
          description,
          destination: selectedDestination.value,
          reference,
          sender: selectedSender.value,
          request: presetRequest,
        })
      );
    },
    [
      dispatch,
      amount,
      presetCurrency,
      date,
      description,
      selectedDestination,
      reference,
      selectedSender,
      presetRequest,
    ]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitNewTransfer}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Accept Amount</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <InputContainer>
          <Selector
            label="Request"
            shrinked
            options={request}
            isDisabled={true}
            value={selectedRequest}
            onChange={(e) => setSelectedRequest(e)}
          />
          <InputBox
            type="date"
            label="Date *"
            shrinked
            onChange={(e) => setDate(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <Selector
            label="Sender *"
            shrinked
            options={sender}
            value={selectedSender}
            onChange={(e) => setSelectedSender(e)}
          />
          <Selector
            label="Destination *"
            shrinked
            options={reducedDestinations}
            value={
              Object.keys(assets).length
                ? selectedDestination
                : { label: 'Loading...' }
            }
            onChange={(e) => setSelectedDestination(e)}
            disabled={!Object.keys(assets).length}
          />
        </InputContainer>
        <InputContainer>
          <Selector
            label="Currency *"
            shrinked
            isDisabled={true}
            options={currency}
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e)}
          />
          <AmountInput
            currency={selectedCurrency?.value || 'NONE'}
            shrinked
            label="Amount *"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <InputBox
            label="Description *"
            shrinked
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <InputBox
            label="Reference *"
            shrinked
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </InputContainer>
        <ButtonContainer>
          <PrimaryButton
            type="submit"
            disabled={
              !amount ||
              !selectedCurrency ||
              !date ||
              !description ||
              !selectedDestination ||
              !reference ||
              !selectedSender
            }
          >
            Create
          </PrimaryButton>
          <SecondaryButton type="button" onClick={clearTransaction}>
            Clear
          </SecondaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}
